import { useTitle } from '@/lib/useTitle';
import { useJsonLd } from '@/lib/useJsonLd';
import { faqPageJsonLd } from '@/lib/seo';
import LocalPageSheet from '@/components/LocalPageSheet';
import type { Faq } from '@/components/LocalPageSheet';

export const meta = {
  path: '/lease-my-commercial-property-waterloo-region-guelph',
  title: 'Lease My Commercial Property — Waterloo Region & Guelph',
  description:
    'Landlord representation for vacant industrial, office and retail space in Cambridge, Kitchener, Waterloo and Guelph. Position the space, market it and negotiate the lease with qualified tenants.',
};

const FAQS: Faq[] = [
  {
    q: 'What information do you need to market vacant commercial space?',
    a: 'A useful starting package includes the address, available square footage, permitted or likely uses, asking rent, additional rent or operating-cost information, possession timing, parking, loading, clear height where relevant, and any plans or photos you already have.',
  },
  {
    q: 'Do you handle industrial, office and retail leasing?',
    a: 'Yes. The positioning is different for each. Industrial tenants may care heavily about loading, power, clear height and highway access; office tenants may prioritize layout, transit and parking; retail tenants often focus on frontage, access, signage, co-tenancy and permitted use.',
  },
  {
    q: 'Can you help if my current tenant is leaving?',
    a: 'Yes. The earlier the vacancy is understood, the more time there is to prepare the marketing package, confirm the lease and possession facts, and decide whether the space should be offered as-is or with landlord work or incentives.',
  },
  {
    q: 'How do you screen prospective tenants?',
    a: 'The level of review depends on the deal, but landlord decisions commonly consider the proposed use, business background, financial information where appropriate, lease security, term, inducements requested and whether the use fits the property and zoning.',
  },
  {
    q: 'Do you represent tenants too?',
    a: 'Yes, but not as both landlord and tenant representative on the same transaction. The role and duties on a specific deal are explained before representation begins.',
  },
];

export default function LandlordCommercialLeasing() {
  useTitle(meta.title, meta.description);
  useJsonLd('faq-landlord-commercial-leasing', faqPageJsonLd(FAQS));

  return (
    <LocalPageSheet
      sheetNo="S-07"
      kicker="Owner search — fill vacant commercial space"
      h1="Lease my commercial property."
      intro="Landlord representation for commercial owners in Cambridge, Kitchener, Waterloo and Guelph who need to position vacant space, reach the right tenant pool and negotiate a lease that works beyond the headline rent."
      glyph="industrial"
      audiences={[
        {
          label: 'Vacant now',
          body: 'Send the address, approximate size, property type and what you know about rent and availability. We can work through how the space should be presented and which tenant groups are realistic.',
          ctaLabel: 'Talk through my vacancy',
        },
        {
          label: 'Tenant leaving soon',
          body: 'A coming vacancy can be planned before the keys come back. Timing, access for showings, restoration obligations, landlord work and the next tenant profile can all affect the leasing strategy.',
          ctaLabel: 'Plan the next lease',
        },
      ]}
      localLabel="Landlords across the four-city market"
      localBody={
        <>
          <p>
            A vacancy is not just a listing problem. The asking structure, permitted use, condition, timing and physical features determine which businesses should even be shown the space.
          </p>
          <p>
            Cambridge industrial space may be marketed around loading, power and 401 access; a Kitchener or Waterloo office vacancy may need a different story around layout, parking and transit; a Guelph or core-area retail unit may turn on frontage, access, signage and use compatibility.
          </p>
          <p>
            The objective is to reduce wasted showings and get serious prospects into a lease conversation with the important business terms understood early.
          </p>
        </>
      }
      honestLabel="Rent is only one line of the deal"
      honestBody="Free rent, fixturing periods, tenant-improvement money, security, renewal rights, exclusivity, assignment language and restoration obligations can materially change the economics and risk of a lease. Those terms need to be reviewed together rather than judged only by the face rent."
      faqs={FAQS}
      interest="leasing"
      closingLine="Turn vacant space into a deliberate leasing process."
      related={[
        { to: '/leasing', label: 'Full landlord & tenant representation' },
        { to: '/industrial-space-for-lease-cambridge', label: 'Industrial leasing — Cambridge' },
        { to: '/commercial-property-valuation-waterloo-region-guelph', label: 'Considering a sale instead?' },
      ]}
    />
  );
}
